import { ref } from 'vue';

/**
 * Prevents concurrent invocations of an async handler.
 * The first call sets `isRunning = true`; any subsequent calls made while
 * the first is still in-flight are silently dropped.
 *
 * Usage:
 *   const { isRunning: isSaving, run } = useAsyncAction();
 *   const save = () => run(async () => { await api.save(); });
 *   // template: :disabled="isSaving"
 */
export function useAsyncAction() {
  const isRunning = ref(false);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    if (isRunning.value) return;
    isRunning.value = true;
    try {
      await fn();
    } finally {
      isRunning.value = false;
    }
  };

  return { isRunning, run };
}
